import React from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { COMPANY } from "../lib/content";

const PAGES = {
  "politica-de-privacidade": {
    title: "Política de Privacidade",
    overline: "[ Legal · LGPD ]",
    intro:
      "A SCA Construtora valoriza a transparência e a proteção dos dados pessoais de clientes, parceiros e usuários do site.",
    sections: [
      {
        h: "1. Dados que coletamos",
        body: (
          <>
            <p className="font-semibold text-sca-black mt-4 mb-2">Dados fornecidos pelo usuário</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Nome completo</li>
              <li>E-mail</li>
              <li>Telefone</li>
              <li>Empresa</li>
              <li>Informações sobre a obra (tipo, estágio, investimento estimado)</li>
            </ul>
            <p className="font-semibold text-sca-black mt-5 mb-2">Dados coletados automaticamente</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Endereço IP</li>
              <li>Localização aproximada</li>
              <li>Tipo de navegador e dispositivo</li>
              <li>Páginas acessadas</li>
            </ul>
          </>
        ),
      },
      {
        h: "2. Finalidade do uso dos dados",
        body: (
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Entrar em contato com o usuário</li>
            <li>Realizar diagnóstico técnico de obras</li>
            <li>Enviar propostas comerciais</li>
            <li>Compartilhar conteúdos técnicos e informativos</li>
            <li>Melhorar a experiência no site</li>
            <li>Cumprir obrigações legais</li>
          </ul>
        ),
      },
      {
        h: "3. Compartilhamento de dados",
        body: (
          <>
            <p>A SCA não vende, aluga ou comercializa dados pessoais. Os dados podem ser compartilhados apenas com:</p>
            <ul className="list-disc pl-5 mt-3 space-y-1.5">
              <li>Prestadores de serviços tecnológicos (hospedagem, CRM, automação)</li>
              <li>Parceiros operacionais quando necessário para execução do serviço</li>
              <li>Autoridades legais, quando exigido por lei</li>
            </ul>
          </>
        ),
      },
      {
        h: "4. Armazenamento e segurança",
        body: (
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Acessos não autorizados</li>
            <li>Vazamentos</li>
            <li>Alterações indevidas</li>
            <li>Os dados são armazenados em ambientes seguros e controlados.</li>
          </ul>
        ),
      },
      {
        h: "5. Direitos do titular",
        body: (
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Solicitar acesso aos seus dados</li>
            <li>Corrigir dados incompletos ou incorretos</li>
            <li>Solicitar exclusão</li>
            <li>Revogar consentimento</li>
          </ul>
        ),
      },
      {
        h: "6. Cookies",
        body: (
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Melhorar a navegação</li>
            <li>Analisar comportamento do usuário</li>
            <li>Personalizar conteúdo</li>
            <li>O usuário pode desativar cookies nas configurações do navegador.</li>
          </ul>
        ),
      },
      { h: "7. Alterações nesta política", body: <p>Esta política pode ser atualizada a qualquer momento.</p> },
      {
        h: "8. Contato",
        body: (
          <p>
            Para dúvidas ou solicitações:<br />
            <a href={`mailto:${COMPANY.email}`} className="text-sca-red hover:underline">{COMPANY.email}</a><br />
            <a href={`tel:${COMPANY.phoneRaw}`} className="text-sca-red hover:underline">{COMPANY.phone}</a>
          </p>
        ),
      },
    ],
  },
  "termos-de-uso": {
    title: "Termos de Uso",
    overline: "[ Legal ]",
    intro: "Ao acessar este site, você concorda com os termos e condições descritos abaixo.",
    sections: [
      { h: "1. Objeto", body: <p>Este site tem como objetivo apresentar informações institucionais, conteúdos técnicos e serviços relacionados à gestão de obras.</p> },
      {
        h: "2. Uso do site",
        body: (
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Utilizar o site de forma legal</li>
            <li>Não praticar atividades ilícitas</li>
            <li>Não violar direitos da empresa</li>
          </ul>
        ),
      },
      {
        h: "3. Propriedade intelectual",
        body: (
          <>
            <p>Todo o conteúdo do site é de propriedade da SCA, incluindo:</p>
            <ul className="list-disc pl-5 mt-3 space-y-1.5">
              <li>Textos</li>
              <li>Estrutura</li>
              <li>Marca</li>
              <li>Conteúdo técnico</li>
            </ul>
            <p className="mt-3">É proibida a reprodução sem autorização.</p>
          </>
        ),
      },
      { h: "4. Limitação de responsabilidade", body: <p>As informações do site são de caráter informativo. A SCA não se responsabiliza por decisões tomadas com base apenas no conteúdo publicado.</p> },
      { h: "5. Links externos", body: <p>O site pode conter links para terceiros. A SCA não se responsabiliza por conteúdos externos.</p> },
      { h: "6. Modificações", body: <p>Os termos podem ser alterados sem aviso prévio.</p> },
      { h: "7. Legislação aplicável", body: <p>Este site é regido pelas leis brasileiras.</p> },
      { h: "8. Foro", body: <p>Fica eleito o foro da comarca de Piracicaba/SP para resolução de conflitos.</p> },
    ],
  },
  lgpd: {
    title: "Lei Geral de Proteção de Dados (LGPD)",
    overline: "[ Legal · LGPD ]",
    intro: "A SCA CONSTRUTORA atua em conformidade com a LGPD, garantindo transparência e segurança no tratamento de dados pessoais.",
    sections: [
      {
        h: "Compromisso com a proteção de dados",
        body: (
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Transparência no uso de dados</li>
            <li>Segurança da informação</li>
            <li>Controle de acesso</li>
            <li>Uso responsável das informações</li>
          </ul>
        ),
      },
      { h: "Princípios aplicados", body: <p>Finalidade, Necessidade, Transparência, Segurança, Prevenção.</p> },
      {
        h: "Direitos do titular",
        body: (
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Confirmar a existência de tratamento</li>
            <li>Acessar dados</li>
            <li>Corrigir dados</li>
            <li>Solicitar exclusão</li>
            <li>Revogar consentimento</li>
          </ul>
        ),
      },
      {
        h: "Canal de atendimento LGPD",
        body: (
          <p>
            Para solicitações relacionadas a dados:{" "}
            <a href={`mailto:${COMPANY.email}`} className="text-sca-red hover:underline">{COMPANY.email}</a>
          </p>
        ),
      },
    ],
  },
  "politica-de-cookies": {
    title: "Política de Cookies",
    overline: "[ Legal · Cookies ]",
    intro: "Esta política descreve como utilizamos cookies no site da SCA Construtora.",
    sections: [
      { h: "O que são cookies", body: <p>Cookies são pequenos arquivos armazenados no dispositivo do usuário.</p> },
      { h: "Como utilizamos", body: <p>Navegação, análise de comportamento, personalização.</p> },
      { h: "Tipos de cookies", body: <p>Essenciais, Analíticos, Funcionais.</p> },
      { h: "Controle de cookies", body: <p>O usuário pode configurar seu navegador para bloquear cookies.</p> },
    ],
  },
};

export default function Legal() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").split("/")[0];
  const page = PAGES[slug];
  if (!page) return <Navigate to="/" replace />;

  return (
    <>
      <section className="pt-28 md:pt-36 pb-12 bg-white border-b border-sca-line">
        <div className="sca-container">
          <Reveal>
            <p className="sca-overline mb-6">{page.overline}</p>
            <h1 className="sca-h1 max-w-[20ch]">{page.title}</h1>
            <p className="sca-body mt-8 max-w-3xl">{page.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="sca-section bg-white">
        <div className="sca-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <aside className="lg:col-span-3 lg:sticky lg:top-32 self-start">
            <p className="sca-overline mb-5">[ Documentos legais ]</p>
            <ul className="space-y-3">
              {Object.entries(PAGES).map(([s, p]) => (
                <li key={s}>
                  <Link
                    to={`/${s}`}
                    className={`text-sm transition-colors ${slug === s ? "text-sca-red font-semibold" : "text-sca-black hover:text-sca-red"}`}
                  >
                    {p.title}
                  </Link>
                </li>
              ))}
            </ul>
          </aside>

          <div className="lg:col-span-8 lg:col-start-5 space-y-12">
            {page.sections.map((s, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <h2 className="font-sans text-2xl md:text-3xl font-semibold text-sca-black tracking-tight mb-4">{s.h}</h2>
                <div className="sca-body space-y-3 text-sca-graphite/85">{s.body}</div>
                <div className="mt-6 sca-divider" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
